/* eslint-disable react/no-find-dom-node */
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { connect } from 'react-redux';
import moment from 'moment-timezone';

import { DayPickerSingleDateController as SingleDatePickerBase } from 'react-dates';
import { pageContext, interpolateString, sanitizeInnerHtml, get } from 'ctm-util-functions';

import classNames from 'classnames';

// Import css here to get it into the bundle
import 'react-dates/lib/css/_datepicker.css';

import * as actions from '../actions';

import styles from './date_picker.css';

class _SingleDatePicker extends Component {

    constructor(props) {
        super(props);

        this.dateFormat = 'YYYY-MM-DD';
        this.displayDateFormat = 'DD-MM-YYYY';

        const [date, defaultDate, today] = this.getInitialTimeStates();

        // Read ENV into a local state which can be changed in the unit tests so
        // that snapshots aren't environment-specific.
        const { everestTheme } = pageContext.getProps();
        this.state = { date, defaultDate, today, everestTheme, hidePrev: false, hideNext: false };
        this.initialiseValueAndNavigation();
    }

    componentDidMount() {
        const { defaultDate } = this.state;
        const [minDate, maxDate] = this.getMinAndMaxDates();
        const numMonths = this.getNumberOfDisplayedMonths();
        const sameYear = maxDate.year() === minDate.year();

        if (sameYear) {
            // when multiple months are displayed, date.month() will be the left hand side date, so we need to take into account how many months are being displayed
            this.setState({
                hideNext: defaultDate.month() + numMonths - 1 === maxDate.month(),
                hidePrev: defaultDate.month() === minDate.month()
            });
        }
    }

    /**
     * Get the initial state for the component so that the state can be setup in the constructor
     * @returns {Array} - An array with the date representation of this.props.value OR the next available day, as well as
     *                    a date representation of todays date.
     */
    getInitialTimeStates = () => {
        const { serverTime, value, schema } = this.props;
        const today = moment(serverTime).tz('Australia/Brisbane').startOf('day');
        let date = null;
        let defaultDate = null;

        if (value && schema.field.selectToday) {
            defaultDate = moment(value, this.dateFormat).tz('Australia/Brisbane');
            date = defaultDate;
        } else if (schema.field.selectToday) {
            defaultDate = this.getNextAvailableDay(today);
            date = schema.field.disableDefaultStartDate ? null : defaultDate;
        } else {
            const daysAhead = schema.field.daysAhead;
            defaultDate = today.clone().add(daysAhead, 'days');
            date = defaultDate;
        }
        return [date, defaultDate, today];
    };

    /**
     * Set the resolved value to app state and configure the navigation buttons
     */
    initialiseValueAndNavigation = () => {
        this.setValue();
    };

    /**
     * Datepicker onChange handler.
     * Used to get the new selected date from the calendar.
     * @param  {Object} newDate - the selected date.
     */
    handleDateChange = (newDate) => {
        const date = moment(newDate).tz('Australia/Brisbane');
        this.setState({ date }, () => {
            this.setValue();
        });
    };

    /**
     * Set the Value for the main field in the form state.
     */
    setValue = () => {
        const { date } = this.state;
        const { dispatch, schema } = this.props;
        dispatch(
            actions.setFormValue(
                schema.backendIdentifier || schema.identifier,
                date && date.format(this.dateFormat),
                schema.stateKey,
                true
            )
        );
    };

    /**
     * Get the next available day from today - or today if today isn't a blocked day
     * @param {Moment} date - the current day to test
     * @returns {Moment} - The next available day
     */
    getNextAvailableDay = (date) => {
        let blockedDays = 0;

        while (this.isDayBlocked(date)) {
            // eslint-disable-next-line no-param-reassign
            date = date.add({ days: 1 });

            // this condition is to avoid infinite loop if all the days are blocked ie. 0 - 6
            if (blockedDays === 365) {
                break;
            }
            blockedDays += 1;
        }
        return date;
    };

    /**
     * Returns the min and max dates from the specified range
     * @returns {Array} - Array containing the min and max dates
     */
    getMinAndMaxDates = () => {
        const { today } = this.state;
        const { min, max } = get(this.props, 'schema.field.range', {});
        const minDate = min ? today.clone().add(min) : today;
        const maxDate = max ? today.clone().add(max) : today;
        return [minDate, maxDate];
    };

    /**
     * Returns true is a specified date is outside of the allowed range for the datepicker. The allowed range is specified
     * in the schema. Currently the range is relative to the start date, eg {months: 1}, {days: 20} etc
     * @param {Moment} date - The date to check
     * @returns {Boolean} - True if the date is within the allowed range, false otherwise.
     */
    isOutsideRange = (date) => {
        const [minDate, maxDate] = this.getMinAndMaxDates();
        return date < minDate || date > maxDate;
    };

    /**
     * Returns true if the specified date is not allowed to be selected
     * @param {Moment} date - The date to check
     * @returns {Boolean} - True if the date should be able to be selected, false otherwise
     */
    isDayBlocked = (date) => {
        const { schema } = this.props;
        if (!schema.field.disallowedDays) {
            return false;
        }
        // date.day() holds true for the week days in all the months
        // date.format('YYYY-MM-DD') tolds true for the specific blocked dates
        // we would like to block specific days, weekends & public holidays. This prop is only used in energy.
        return schema.field.disallowedDays.includes(date.format('YYYY-MM-DD')) || schema.field.disallowedDays.includes(date.day());
    };

    /**
     * Focus change handler used by the datepicker. If alwaysOpen is false or not specified, update the focus state
     * on each focus changem otherwise always keep focus set to true.
     *
     * If the focus has changed from true to false, send a touch.
     * @param {Boolean} focused - True if the input element is focused
     */
    onFocus = (focused) => {
        const { dispatch, schema } = this.props;
        if (!focused) {
            // when focus has gone from true to false, we send the touch
            dispatch(actions.elementTouched(schema.identifier, schema.stateKey));
        }
    };

    /**
     * Used by the datepicker to render the info panel below the calendar
     * @returns {Node|null} - A div containing the info panel message, or null if no infoText has been specified
     */
    renderCalendarInfo = () => {
        const { date, everestTheme } = this.state;
        const { schema } = this.props;
        if (!schema.field.infoText) {
            return null;
        }

        let infoText = schema.field.infoTextNoDate;
        if (date) {
            const args = { dateStr: date.format(this.displayDateFormat) };
            infoText = interpolateString(schema.field.infoText)(args);
        }
        // eslint-disable-next-line react/no-danger
        const content = <span dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(infoText) }} />;
        if (everestTheme === 'choo') {
            return <div className={styles.info_panel_choo}>{content}</div>;
        }
        return <div className={styles.info_panel}>{content}</div>;
    };

    /**
     * Returns the number of days in the specified range
     * @param {Moment} date - An optional date to guarantee results for unit tests
     * @returns {Number} - The number of days in the specified range
     */
    getNumberOfDaysInRange = (date = null) => {
        const [minDate, maxDate] = this.getMinAndMaxDates(date);
        return (maxDate - minDate) / 86400000; // 86400000 is one day in milliseconds
    };

    /**
     * Return the number of months that should be displayed. If there are 30 or more days remaining in the month from today,
     * only show one month, otherwise show the configured number of months.
     * @param {Moment} date - An optional date to guarantee results for unit tests
     * @returns {Number} - The number of months to display
     */
    getNumberOfDisplayedMonths = (date = null) => {
        const { breakpoint, schema } = this.props;
        // only show one month on xs
        if (breakpoint === 'xs') {
            return 1;
        }

        let numMonths = schema.field.numMonths;
        // we only want to show a single month if the datepicker range is 30 days or less and the whole range is still
        // available in a single month - ie today is the first day of a 30 or 31 day month
        const rangeDays = this.getNumberOfDaysInRange(date);
        if (rangeDays > 30) {
            return numMonths;
        }

        let today = moment().tz('Australia/Brisbane').startOf('day');
        if (date !== null) {
            today = date.startOf('day');
        }

        if (today.daysInMonth() - today.date() >= rangeDays) {
            numMonths = 1;
        }
        return numMonths;
    };

    /**
     * Callback that runs after the next or previous buttons have been clicked, figures out if the displayed dates are within
     * the datepickers range and then hide the navigation buttons as appropriate
     * @param {Moment} currentDate - The new date passed from react-dates when a button is clicked
     */
    onMonthClick = (currentDate) => {
        const [minDate, maxDate] = this.getMinAndMaxDates();

        const numMonths = this.getNumberOfDisplayedMonths();
        const sameMaxYear = maxDate.year() === currentDate.year();
        const sameMinYear = minDate.year() === currentDate.year();

        // when multiple months are displayed, date.month() will be the left hand side date, so we need to take into account how many months are being displayed
        this.setState({
            hideNext: sameMaxYear && currentDate.month() + numMonths - 1 >= maxDate.month(),
            hidePrev: sameMinYear && currentDate.month() === minDate.month()
        });
    };

    /**
     * Get initial visible month of the date picker. If a date is on state it would have
     * already been through the rules engine so we can check for a validation result. If
     * the date is invalid simply return the current date.
     * @returns {Moment} - initial visible month
     */
    getInitialVisibleMonth = () => {
        const { defaultDate } = this.state;
        const validSelectedDate = get(this.props, 'rules.validation.result', false);
        return validSelectedDate ? defaultDate : moment().tz('Australia/Brisbane');
    };

    /**
     * Update this component's onFocus
     * @param {Object} args - the arguments
     * @param {Boolean} focused - true if it is focused, otherwise false
     */
    onFocusChange = ({ focused }) => {
        this.onFocus(focused);
    };

    render = () => {
        const { date, everestTheme, hidePrev, hideNext } = this.state;
        const { schema, breakpoint } = this.props;
        let id = `date_input_${schema.identifier}`;

        const isXS = breakpoint === 'xs';
        if (isXS && !schema.field.showOnXS) {
            return null;
        } if (isXS && schema.field.showOnXS) {
            // change the id (which is used as the key for the DatePicker) so that the datepicker re-mounts for the new
            // breakpoint. This avoids issues with styling / orientation changes on breakpoint changes
            id = `${id}_xs`;
        }

        const choosiClass = classNames(styles.date_picker_container, styles.DayPicker);

        return (
            <div className={everestTheme === 'choo' ? choosiClass : styles.date_picker_container}>
                <SingleDatePickerBase
                    key={id}
                    hideKeyboardShortcutsPanel
                    date={date}
                    initialVisibleMonth={this.getInitialVisibleMonth}
                    numberOfMonths={this.getNumberOfDisplayedMonths()}
                    isDayBlocked={this.isDayBlocked}
                    isOutsideRange={this.isOutsideRange}
                    onDateChange={this.handleDateChange}
                    onFocusChange={this.onFocusChange}
                    renderCalendarInfo={this.renderCalendarInfo}
                    enableOutsideDays={false}
                    focused
                    keepOpenOnDateSelect
                    onPrevMonthClick={this.onMonthClick}
                    onNextMonthClick={this.onMonthClick}
                    navPrev={hidePrev && <div />}
                    navNext={hideNext && <div />}
                />
            </div>
        );
    };

}

_SingleDatePicker.propTypes = {
    schema: PropTypes.object.isRequired,
    value: PropTypes.string,
    // props.rules is used, but inside a get(), so the linter trips up on it
    // eslint-disable-next-line react/no-unused-prop-types
    rules: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    breakpoint: PropTypes.string.isRequired,
    serverTime: PropTypes.string
};

/**
 * Add the breakpoint to props
 * @param {Object} state - The app state
 * @returns {Object} - The mapped props
 */
const mapStateToProps = (state) => {
    return {
        breakpoint: state.get('browser').mediaType,
        serverTime: state.getIn(['page', 'serverTime'])
    };
};

export const SingleDatePicker = connect(mapStateToProps)(_SingleDatePicker);
export { _SingleDatePicker };
